import React, { FC, Fragment, useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Unstable_Grid2 as Grid,
  List,
  ListItem,
  ListSubheader,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { flattenDeep, values as _values } from 'lodash';

import { LeaderboardConfig, LeaderboardEventScores } from 'api';

import ScoringItem from './ScoringItem';

interface Props {
  category: string;
}

const ScoringList: FC<Props> = ({ category }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<LeaderboardConfig>();
  const { scoring_aggregate } = values;
  const events = scoring_aggregate[category];

  const [open, setOpen] = useState(false);
  const [totalPoints, setTotalPoints] = useState(0);

  useEffect(() => {
    const eventsList = flattenDeep(_values(events));
    const scores = eventsList.reduce((acc: number, item: LeaderboardEventScores) => {
      if (item.score !== '') {
        return acc + parseFloat(item.score);
      }
      return acc;
    }, 0);

    setTotalPoints(scores);
  }, [events]);

  function onClick() {
    setOpen(!open);
  }

  function renderItems(item: LeaderboardEventScores, index: number, subCategory: string) {
    return (
      <ScoringItem
        key={item.event + index}
        item={item}
        index={index}
        bucket={`scoring_aggregate[${category}][${subCategory}]`}
      />
    );
  }

  function renderEvents() {
    return Object.entries(events).map(([key], idx) => {
      return (
        <Fragment key={key + idx}>
          {key !== 'general' && (
            <Box marginBottom={1} marginTop={2}>
              <ListSubheader disableGutters>
                <Typography variant="body2">{key}</Typography>
              </ListSubheader>
            </Box>
          )}

          {events[key].map((item: LeaderboardEventScores, index: number) =>
            renderItems(item, index, key)
          )}
        </Fragment>
      );
    });
  }

  return (
    <Box marginY={2}>
      <List disablePadding>
        <Box marginBottom={1}>
          <ListItem onClick={onClick} disableGutters>
            <Grid xs={12} container justifyContent="space-between">
              <Grid xs={9}>
                <Typography variant="subtitle2">{t(`admin:${category}`)}</Typography>
              </Grid>
              <Grid xs={3} container justifyContent="flex-end" alignItems="center">
                <Box marginRight={1}>
                  <Typography color={open ? 'primary' : 'textPrimary'}>
                    {totalPoints.toFixed(1)}
                  </Typography>
                </Box>
                {open ? <ExpandLess color="primary" /> : <ExpandMore />}
              </Grid>
            </Grid>
          </ListItem>
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {renderEvents()}
          </List>
        </Collapse>

        <Box marginY={3}>
          <Divider />
        </Box>
      </List>
    </Box>
  );
};

export default ScoringList;
